import { useEffect } from 'react';
import { useNetwork } from 'wagmi';
import { getThemeForNetwork, isAutoThemeSwitchEnabled } from '@/utils/networkThemeMapping';

/**
 * Hook to automatically sync theme with network changes
 * This will apply the network-specific theme when the user switches networks,
 * but won't interfere if the user manually changes themes afterward.
 */
export function useNetworkThemeSync() {
  const { chain } = useNetwork();

  useEffect(() => {
    // Only proceed if auto theme switching is enabled and we have a chain
    if (!isAutoThemeSwitchEnabled() || !chain?.id) {
      return;
    }

    // Get the theme for the current network
    const networkTheme = getThemeForNetwork(chain.id);
    
    if (!networkTheme) {
      // If no specific theme for this network, don't change anything
      return;
    }

    // Check if we should apply the theme
    // We only auto-switch if the user hasn't manually changed themes recently
    const lastManualThemeChange = localStorage.getItem('lastManualThemeChange');
    
    // If there was a recent manual theme change (within 30 seconds), don't auto-switch
    if (lastManualThemeChange) {
      const manualChangeTime = parseInt(lastManualThemeChange);
      const now = Date.now();
      if (now - manualChangeTime < 30000) { // 30 seconds
        return;
      }
    }

    // Apply the network theme
    applyNetworkTheme(networkTheme);
    
    // Mark this as a network-triggered theme change
    localStorage.setItem('lastNetworkThemeChange', Date.now().toString());
    
  }, [chain?.id]);
}

/**
 * Apply a theme based on network switching
 */
function applyNetworkTheme(themeName: string) {
  try {
    // Import the theme application logic from SwatchPro
    // We'll dispatch a custom event that SwatchPro can listen to
    const event = new CustomEvent('applyNetworkTheme', {
      detail: { themeName }
    });
    window.dispatchEvent(event);
    
    // Also update localStorage to reflect the current theme
    localStorage.setItem('currentThemeName', themeName);
    
    console.log(`Auto-switched to ${themeName} theme for network`);
  } catch (error) {
    console.error('Failed to apply network theme:', error);
  }
}

/**
 * Mark that a manual theme change occurred
 * This prevents auto-switching for a short period
 */
export function markManualThemeChange() {
  localStorage.setItem('lastManualThemeChange', Date.now().toString());
}
